// Shared normalizers for the Investment/Payment/Payout resource shapes used
// by both the admin (adminInvestmentsApi.js) and investor
// (investorInvestmentsApi.js) API slices — the objects are identical
// either side, just scoped differently server-side.
//
// Monetary fields arrive as both a decimal string (display-ready) and a
// `_minor` integer/kobo version (safe for math, e.g. progress bars). Both
// are kept — never derive one from the other client-side.

function normalizeClosedBy(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') return { id: null, fullName: raw };
  return {
    id: raw.uuid ?? raw.id ?? null,
    fullName: [raw.first_name, raw.last_name].filter(Boolean).join(' ') || raw.full_name || raw.email || null,
  };
}

// v1.17.0 — generic "who did this" reference used across rollover fields
// (scheduled_by, requested_by, executed_by, cancelled_by). The rollover docs
// specify a plain { uuid, name } shape (simpler than closed_by's
// first_name/last_name pair), but this stays defensive about either shape
// showing up since the backend's admin-reference shape isn't 100% uniform
// across every endpoint in this API.
function normalizePersonRef(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') return { id: null, name: raw };
  return {
    id: raw.uuid ?? raw.id ?? null,
    // `.join(' ')` always returns a string (possibly '') even when both
    // name parts are missing, so this chain uses `||` rather than `??` —
    // an empty string must fall through to full_name/email, not win just
    // because it's not null/undefined.
    name: raw.name || [raw.first_name, raw.last_name].filter(Boolean).join(' ') || raw.full_name || raw.email || null,
  };
}

// v1.17.0 — Investment Rollover eligibility/state block, returned inline as
// `rollover` on single-record investment GETs only (never on list
// responses — don't build list/card UI that expects this). `can_rollover`
// and `state`/`reason` are mutually informative: when can_rollover is
// false, `reason` is the backend's human-readable explanation and must be
// surfaced as-is, not replaced with a generic message. `scheduled_rollover`
// is non-null only when a rollover is queued to auto-execute (on final
// interest payout or the nightly sweep) rather than executed immediately —
// its presence is what distinguishes the "scheduled" UI state from the
// plain "eligible, not yet requested" state.
export function normalizeRollover(raw) {
  if (!raw) return null;
  return {
    canRollover: !!raw.can_rollover,
    state: raw.state ?? null,
    reason: raw.reason ?? null,
    windowOpensAt: raw.window_opens_at ?? null,
    windowDays: Number(raw.window_days ?? 0),
    maxRollovers: Number(raw.max_rollovers ?? 0),
    timesRolledOver: Number(raw.times_rolled_over ?? 0),
    scheduledRollover: raw.scheduled_rollover
      ? {
          scheduledAt: raw.scheduled_rollover.scheduled_at ?? null,
          scheduledBy: normalizePersonRef(raw.scheduled_rollover.scheduled_by),
        }
      : null,
  };
}

// v1.17.0 — one row of GET /admin|investor/investments/{investment}/rollovers
// history. `previous`/`new` are before/after snapshots of the term that was
// extended — kept as nested objects (never flattened/merged) so a history
// row can render a clean two-column comparison without field-name
// collisions between the two terms.
function normalizeRolloverTermSnapshot(raw) {
  if (!raw) return null;
  return {
    durationInDays: Number(raw.duration_in_days ?? 0),
    maturityDate: raw.maturity_date ?? null,
    totalInterestMinor: Number(raw.total_interest_minor ?? 0),
    totalInterest: raw.total_interest ?? '0.00',
    payoutPeriodsTotal: Number(raw.payout_periods_total ?? 0),
    interestAddedMinor: raw.interest_added_minor != null ? Number(raw.interest_added_minor) : null,
    interestAdded: raw.interest_added ?? null,
  };
}

export function normalizeRolloverHistoryEntry(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    sequence: Number(raw.sequence ?? 0),
    state: raw.state ?? null,
    note: raw.note ?? null,
    requestedBy: normalizePersonRef(raw.requested_by),
    requestedAt: raw.requested_at ?? null,
    executedAt: raw.executed_at ?? null,
    executedAutomatically: !!raw.executed_automatically,
    executedBy: normalizePersonRef(raw.executed_by),
    cancelledAt: raw.cancelled_at ?? null,
    cancelledBy: normalizePersonRef(raw.cancelled_by),
    cancelReason: raw.cancel_reason ?? null,
    cancelledAutomatically: !!raw.cancelled_automatically,
    principalMinor: Number(raw.principal_minor ?? 0),
    principal: raw.principal ?? '0.00',
    roiPercentage: Number(raw.roi_percentage ?? 0),
    interestPaidMinorAtExecution: raw.interest_paid_minor_at_execution != null ? Number(raw.interest_paid_minor_at_execution) : null,
    principalPaidMinorAtExecution: raw.principal_paid_minor_at_execution != null ? Number(raw.principal_paid_minor_at_execution) : null,
    previous: normalizeRolloverTermSnapshot(raw.previous),
    new: normalizeRolloverTermSnapshot(raw.new),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

// PayoutSummary (v1.16.0) — the authoritative ledger snapshot returned
// top-level as `summary` on both payout-list GETs and inlined as
// `payout_summary` on investment objects. Every figure here comes straight
// from the backend and must be displayed as-is — never recomputed by
// summing raw payout rows client-side (that's explicitly the bug this
// feature replaces; see InvestmentDetail.jsx's old
// remainingPayoutPrincipalMinor derivation).
export function normalizePayoutSummary(raw) {
  if (!raw) return null;
  return {
    frequency: raw.frequency ?? null,
    frequencyLabel: raw.frequency_label ?? null,
    totalPeriods: Number(raw.total_periods ?? 0),
    periodsElapsed: Number(raw.periods_elapsed ?? 0),
    currentPeriod: Number(raw.current_period ?? 0),
    principalPaid: !!raw.principal_paid,
    totalPaidOutMinor: Number(raw.total_paid_out_minor ?? 0),
    totalPaidOut: raw.total_paid_out ?? '0.00',
    principalPaidAmountMinor: Number(raw.principal_paid_amount_minor ?? 0),
    principalPaidAmount: raw.principal_paid_amount ?? '0.00',
    interestPaidAmountMinor: Number(raw.interest_paid_amount_minor ?? 0),
    interestPaidAmount: raw.interest_paid_amount ?? '0.00',
    totalPaymentDueMinor: Number(raw.total_payment_due_minor ?? 0),
    totalPaymentDue: raw.total_payment_due ?? '0.00',
    totalPaymentDueBreakdown: {
      principalMinor: Number(raw.total_payment_due_breakdown?.principal_minor ?? 0),
      principal: raw.total_payment_due_breakdown?.principal ?? '0.00',
      interestMinor: Number(raw.total_payment_due_breakdown?.interest_minor ?? 0),
      interest: raw.total_payment_due_breakdown?.interest ?? '0.00',
    },
    currentPeriodAmountMinor: Number(raw.current_period_amount_minor ?? 0),
    currentPeriodAmount: raw.current_period_amount ?? '0.00',
    accruedInterestToDateMinor: Number(raw.accrued_interest_to_date_minor ?? 0),
    accruedInterestToDate: raw.accrued_interest_to_date ?? '0.00',
    totalInterestMinor: Number(raw.total_interest_minor ?? 0),
    totalInterest: raw.total_interest ?? '0.00',
    // No decimal-string sibling documented for this one — format from minor
    // at the display layer (see formatMinorCurrency in InvestmentDetail.jsx).
    outstandingInterestMinor: Number(raw.outstanding_interest_minor ?? 0),
    hasPendingPayout: !!raw.has_pending_payout,
    nextPayoutDueAt: raw.next_payout_due_at ?? null,
    isPaidOut: !!raw.is_paid_out,
  };
}

export function normalizeInvestment(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    userUuid: raw.user_uuid ?? null,
    investmentPlanUuid: raw.investment_plan_uuid ?? null,
    principal: raw.principal ?? '0.00',
    principalMinor: Number(raw.principal_minor ?? 0),
    roiPercentage: Number(raw.roi_percentage ?? 0),
    durationInDays: Number(raw.duration_in_days ?? 0),
    isOverride: !!raw.is_override,
    customRoiPercentage: raw.custom_roi_percentage ?? null,
    customDurationInDays: raw.custom_duration_in_days ?? null,
    fundingMethod: raw.funding_method ?? null,
    status: raw.status ?? null,
    offlineStatus: raw.offline_status ?? null,
    fundedAmount: raw.funded_amount ?? '0.00',
    fundedAmountMinor: Number(raw.funded_amount_minor ?? 0),
    remainingAmount: raw.remaining_amount ?? '0.00',
    remainingAmountMinor: Number(raw.remaining_amount_minor ?? 0),
    startDate: raw.start_date ?? null,
    maturityDate: raw.maturity_date ?? null,
    createdAt: raw.created_at ?? null,

    // v1.16.0 — payout structure fields, present on both list and detail,
    // admin and investor.
    payoutFrequency: raw.payout_frequency ?? null,
    payoutPeriodsTotal: Number(raw.payout_periods_total ?? 0),
    nextPayoutDueAt: raw.next_payout_due_at ?? null,
    principalIsPaid: !!raw.principal_is_paid,
    payoutSummary: normalizePayoutSummary(raw.payout_summary),

    // v1.16.0 — newly-documented ledger/closure fields.
    totalInterestMinor: Number(raw.total_interest_minor ?? 0),
    totalInterest: raw.total_interest ?? '0.00',
    principalPaidMinor: Number(raw.principal_paid_minor ?? 0),
    principalPaid: raw.principal_paid ?? '0.00',
    interestPaidMinor: Number(raw.interest_paid_minor ?? 0),
    interestPaid: raw.interest_paid ?? '0.00',
    isPaidOut: !!raw.is_paid_out,
    closedAt: raw.closed_at ?? null,
    closeReason: raw.close_reason ?? null,
    closedBy: normalizeClosedBy(raw.closed_by),

    // v1.17.0 — Investment Rollover fields. Present on single-record GETs
    // only (admin and investor detail), absent from list responses — any
    // code reading these off a list-fetched investment will silently get
    // the zero/null defaults below, which is why list/card UI must not
    // attempt to show rollover-eligibility badges.
    //
    // `totalInterestMinor`/`totalInterest` above become the LIFETIME total
    // across all terms once rolloverCount > 0 — `currentTermInterestMinor`/
    // `currentTermInterest` below are the running term's figure only. Never
    // conflate the two; label both explicitly wherever they appear near
    // each other (see InvestmentDetail.jsx).
    rolloverTermDays: Number(raw.rollover_term_days ?? 0),
    rolloverCount: Number(raw.rollover_count ?? 0),
    currentTermStartedAt: raw.current_term_started_at ?? null,
    currentTermInterestMinor: Number(raw.current_term_interest_minor ?? 0),
    currentTermInterest: raw.current_term_interest ?? '0.00',
    rollover: normalizeRollover(raw.rollover),
  };
}

export function normalizePayment(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    investmentUuid: raw.investment_uuid ?? null,
    amount: raw.amount ?? '0.00',
    amountMinor: Number(raw.amount_minor ?? 0),
    method: raw.method ?? null,
    provider: raw.provider ?? null,
    providerReference: raw.provider_reference ?? null,
    status: raw.status ?? null,
    note: raw.note ?? null,
    createdAt: raw.created_at ?? null,
  };
}

// Payouts (v1.13.0) are a distinct concept from payments — money going back
// out to the investor (principal returned + interest/ROI), not money coming
// in to fund the investment. Different table, different fields: a
// principal/interest split instead of a single amount, a `paid_at` date
// instead of just created_at, and a `recorded_by` admin. Never conflate
// with normalizePayment — the shapes aren't interchangeable.
//
// v1.16.0: every payout row now carries an explicit `type` ('principal' |
// 'interest') stating which side of the stored principal/interest pair is
// the real one for that row (the other side is 0). Always read `type`
// directly to label/render a row — never infer it by checking which of
// principal/interest is non-zero, and never "correct" a type that looks
// inconsistent with the amount. The backend states the type; the frontend
// trusts it as-is.
export function normalizePayout(raw) {
  if (!raw) return null;
  const recordedBy = raw.recorded_by;
  const recordedByName = recordedBy
    ? [recordedBy.first_name, recordedBy.last_name].filter(Boolean).join(' ') || recordedBy.email || null
    : null;
  return {
    id: raw.uuid ?? raw.id,
    type: raw.type ?? null,
    principal: raw.principal ?? '0.00',
    principalMinor: Number(raw.principal_minor ?? 0),
    interest: raw.interest ?? '0.00',
    interestMinor: Number(raw.interest_minor ?? 0),
    paidAt: raw.paid_at ?? null,
    note: raw.note ?? null,
    recordedByName,
    createdAt: raw.created_at ?? null,
  };
}
